/**
 * Homly Status & Priority Badges
 * ==============================
 * Source unique pour afficher les statuts/priorités à travers toute l'app.
 * Vocabulaire métier français, pas de jargon technique.
 */
import React from 'react';
import { AlertCircle, CheckCircle2, Clock, FileEdit, FileSignature, TrendingDown, Archive } from 'lucide-react';

// ─── STATUTS DE MANDAT ──────────────────────────────────────────────────
//
// Vocabulaire homogène à travers toute l'app, conforme au brief :
// À compléter / En attente de signature / Signé / Suivi en cours / À risque / Vendu / Archivé
//
const STATUS_CONFIG = {
    draft:       { label: 'À compléter',           icon: FileEdit,       color: 'var(--homly-status-draft)' },
    generated:   { label: 'À compléter',           icon: FileEdit,       color: 'var(--homly-status-draft)' },
    sent:        { label: 'En attente de signature', icon: FileSignature, color: 'var(--homly-status-pending)' },
    pending:     { label: 'En attente de signature', icon: FileSignature, color: 'var(--homly-status-pending)' },
    acknowledged:{ label: 'Signé',                 icon: CheckCircle2,   color: 'var(--homly-status-active)' },
    signed:      { label: 'Signé',                 icon: CheckCircle2,   color: 'var(--homly-status-active)' },
    active:      { label: 'Suivi en cours',        icon: Clock,          color: 'var(--homly-status-progress)' },
    actif:       { label: 'Suivi en cours',        icon: Clock,          color: 'var(--homly-status-progress)' },
    at_risk:     { label: 'À risque',              icon: AlertCircle,    color: 'var(--homly-status-risk)' },
    risque:      { label: 'À risque',              icon: AlertCircle,    color: 'var(--homly-status-risk)' },
    vendu:       { label: 'Vendu',                 icon: TrendingDown,   color: 'var(--homly-status-sold)' },
    sold:        { label: 'Vendu',                 icon: TrendingDown,   color: 'var(--homly-status-sold)' },
    archived:    { label: 'Archivé',               icon: Archive,        color: 'var(--homly-status-archived)' },
    resilie:     { label: 'Résilié',               icon: Archive,        color: 'var(--homly-status-archived)' },
};

const SIZES = {
    sm: { px: 8, py: 2, fontSize: 10, iconSize: 11, gap: 4, borderRadius: 6 },
    md: { px: 10, py: 4, fontSize: 11, iconSize: 13, gap: 6, borderRadius: 8 },
    lg: { px: 12, py: 6, fontSize: 13, iconSize: 14, gap: 6, borderRadius: 10 },
};

export function StatusBadge({ status, size = 'md', className = '', dataTestid }) {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const s = SIZES[size];
    const Icon = config.icon;
    return (
        <span
            className={className}
            data-testid={dataTestid || `status-badge-${status}`}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: s.gap,
                padding: `${s.py}px ${s.px}px`,
                fontFamily: 'var(--homly-font-display)',
                fontSize: s.fontSize,
                fontWeight: 600,
                letterSpacing: '0.02em',
                color: config.color,
                background: 'transparent',
                border: `1px solid ${config.color}55`,
                borderRadius: s.borderRadius,
                lineHeight: 1,
                whiteSpace: 'nowrap',
            }}
        >
            <Icon size={s.iconSize} strokeWidth={2.2} aria-hidden="true" />
            {config.label}
        </span>
    );
}

// ─── PRIORITY BADGES ────────────────────────────────────────────────────
//
// Priorité d'une action : critical (urgent) / high (à faire aujourd'hui) /
// medium (cette semaine) / low (quand vous voulez) / ok (rien à faire)
//
const PRIORITY_CONFIG = {
    critical: { label: 'Urgent',           color: 'var(--homly-priority-critical)', bg: 'var(--homly-priority-critical-bg)' },
    high:     { label: "À faire aujourd'hui", color: 'var(--homly-priority-high)',  bg: 'var(--homly-priority-high-bg)' },
    medium:   { label: 'Cette semaine',    color: 'var(--homly-priority-medium)',   bg: 'var(--homly-priority-medium-bg)' },
    low:      { label: 'Quand possible',   color: 'var(--homly-priority-low)',      bg: 'var(--homly-priority-low-bg)' },
    ok:       { label: 'Tout est bon',     color: 'var(--homly-priority-ok)',       bg: 'var(--homly-priority-ok-bg)' },
};

export function PriorityBadge({ priority, size = 'md', label, className = '', dataTestid }) {
    const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
    const s = SIZES[size];
    const displayLabel = label || config.label;
    return (
        <span
            className={className}
            data-testid={dataTestid || `priority-badge-${priority}`}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: s.gap,
                padding: `${s.py}px ${s.px}px`,
                fontFamily: 'var(--homly-font-display)',
                fontSize: s.fontSize,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: config.color,
                background: config.bg,
                border: `1px solid ${config.color}55`,
                borderRadius: s.borderRadius,
                lineHeight: 1,
                whiteSpace: 'nowrap',
            }}
        >
            <span
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: config.color,
                    boxShadow: `0 0 8px ${config.color}`,
                    flexShrink: 0,
                }}
                aria-hidden="true"
            />
            {displayLabel}
        </span>
    );
}

export { STATUS_CONFIG, PRIORITY_CONFIG };
